import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, CardContent, Container, FormControl, Grid, MenuItem, Select, Typography } from '@mui/material';
import axiosInstance from '../../services/axiosInstance';
import FiltersBar from '../../components/progress/FiltersBar';
import SubmissionTable from '../../components/progress/SubmissionTable';
import ProblemDrawer from '../../components/progress/ProblemDrawer';
import SummaryCards from '../../components/progress/SummaryCards';
import DifficultyBreakdown from '../../components/progress/DifficultyBreakdown';
import LanguageBreakdown from '../../components/progress/LanguageBreakdown';
import InsightsPanel from '../../components/progress/InsightsPanel';
import UnderConstruction from '../../components/progress/UnderConstruction';
import { PLATFORMS } from '../../constants/platforms';

const ProgressPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [platform, setPlatform] = useState('leetcode');
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({});
  const [selectedProblemId, setSelectedProblemId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [streakData, setStreakData] = useState(null);
  const [streakLoading, setStreakLoading] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        ...filters,
      };

      const response = await axiosInstance.get('/student/progress/submissions', { params });
      const payload = response?.data || {};

      setSubmissions(payload.data || []);
      setTotal(payload.pagination?.total || 0);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    if (platform !== 'leetcode') return;
    fetchSubmissions();
  }, [fetchSubmissions, platform]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const response = await axiosInstance.get('/student/progress/summary');
      setSummaryData(response?.data || null);
    } catch (error) {
      console.log(error);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (platform !== 'leetcode') return;
    fetchSummary();
  }, [fetchSummary, platform]);

  const fetchTrend = useCallback(async () => {
    setTrendLoading(true);
    try {
      const response = await axiosInstance.get('/student/progress/trend');
      setTrendData(response?.data || null);
    } catch (error) {
      console.log(error);
    } finally {
      setTrendLoading(false);
    }
  }, []);

  useEffect(() => {
    if (platform !== 'leetcode') return;
    fetchTrend();
  }, [fetchTrend, platform]);

  const fetchStreak = useCallback(async () => {
    setStreakLoading(true);
    try {
      const response = await axiosInstance.get('/student/progress/streak');
      setStreakData(response?.data || null);
    } catch (error) {
      console.log(error);
    } finally {
      setStreakLoading(false);
    }
  }, []);

  useEffect(() => {
    if (platform !== 'leetcode') return;
    fetchStreak();
  }, [fetchStreak, platform]);

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          mt: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Progress
          </Typography>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select value={platform} onChange={(e) => setPlatform(e.target.value)}>
              {PLATFORMS.map((item) => (
                <MenuItem key={item.key} value={item.key} disabled={!item.enabled}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {platform === 'leetcode' ? (
          <>
            <InsightsPanel
              insightsData={{
                weakAreas: summaryData?.weakAreas || [],
                trend: trendData,
                streak: streakData,
                suggestion:
                  trendLoading || streakLoading
                    ? 'Loading insights'
                    : trendData?.trend === 'down'
                      ? 'Focus on revision for weak topics'
                      : 'Keep the current momentum',
              }}
            />

            <SummaryCards data={summaryData} loading={summaryLoading} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ py: 1.75 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
                      Difficulty Breakdown
                    </Typography>
                    <DifficultyBreakdown
                      data={summaryData?.difficultyBreakdown}
                      difficultyBreakdown={summaryData?.difficultyBreakdown}
                      loading={summaryLoading}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ py: 1.75 }}>
                    <Typography variant="h5" sx={{ mb: 1.25, fontWeight: 700 }}>
                      Top Languages
                    </Typography>
                    <LanguageBreakdown
                      data={summaryData?.languageBreakdown}
                      languageBreakdown={summaryData?.languageBreakdown}
                      loading={summaryLoading}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            {/* TODO: Insert a new table here to show user progress by topic/tag (Placeholder) */}
            <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
                  Submission History                </Typography>
              
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ py: 2 }}>
                <FiltersBar
                  onApply={(f) => {
                    setFilters(f);
                    setPage(1);
                  }}
                  onReset={() => {
                    setFilters({});
                    setPage(1);
                  }}
                />
              </CardContent>
            </Card>

            <SubmissionTable
              rows={submissions}
              loading={loading}
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={(newPage) => setPage(newPage)}
              onRowClick={(row) => {
                setSelectedProblemId(row.problemId || row.problem_slug || null);
                setDrawerOpen(true);
              }}
            />
            </CardContent>
            </Card>

            <ProblemDrawer
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              problemId={selectedProblemId}
            />
          </>
        ) : (
          <UnderConstruction platform={platform} />
        )}
      </Box>
    </Container>
  );
};

export default ProgressPage;
